import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Table, Switch, Space, ConfigProvider, theme, Button as AntdButton } from "antd";
import { Input } from "@/components/ui/inputForm";
import { EditOutlined, PlusOutlined } from '@ant-design/icons';
import { getTurma, getTurmaById, postTurma } from "@/services/turma";
import { message } from "antd";
import { usePermissions } from "@/hooks/usePermissions";

export default function ConsultaTurma() {
  const [turmas, setTurmas] = useState<any[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("todos");
  const navigate = useNavigate();
  const { canInsert, canEdit } = usePermissions();

  useEffect(() => {
    getTurma().then((res) => {
      if (res.status === 200) {
        const sortedTurmas = res.data
          .map((t: any) => ({ ...t, ativo: t.ativo ?? true }))
          .sort((a: any, b: any) => {
            // Primeiro compara por data de início
            const compareDataInicio = new Date(b.periodoLetivo.dataInicio).getTime() - new Date(a.periodoLetivo.dataInicio).getTime();
            // Se as datas de início forem iguais, compara por data fim
            if (compareDataInicio === 0) {
              return new Date(b.periodoLetivo.dataFim).getTime() - new Date(a.periodoLetivo.dataFim).getTime();
            }
            return compareDataInicio;
          });
        setTurmas(sortedTurmas);
      }
    });
  }, []);

  const handleAdd = useCallback(() => {
    navigate("/turma/cadastro");
  }, [navigate]);

  const handleEdit = (id: string) => {
    navigate(`/turma/${id}`);
  };

  const toggleAtivo = async (id: string) => {
    try {
      const { data: turma } = await getTurmaById(id);
      const updatedTurma = { ...turma, ativo: !turma.ativo };
      const response = await postTurma(updatedTurma);
      if (response.status === 200 || response.status === 201) {
        setTurmas((prev) =>
          prev.map((t) => (t.id === id ? { ...t, ativo: !t.ativo } : t))
        );
        message.success(`Turma ${!turma.ativo ? 'ativada' : 'inativada'} com sucesso!`);
      } else {
        message.error("Falha ao atualizar o status da turma.");
      }
    } catch (error) {
      message.error("Erro ao comunicar com o servidor.");
    }
  };

  const filteredTurmas = turmas.filter((t) => {
    const matchesSearch = t.nome?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus =
      statusFilter === "todos" ||
      (statusFilter === "ativos" && t.ativo) ||
      (statusFilter === "inativos" && !t.ativo);
    return matchesSearch && matchesStatus;
  });

  const columns = [
    {
      title: "Nome",
      dataIndex: "nome",
      key: "nome",
      sorter: (a: any, b: any) => (a.nome ?? "").localeCompare(b.nome ?? ""),
    },
    {
      title: "Data Início",
      dataIndex: ["periodoLetivo", "dataInicio"],
      key: "dataInicio",
      render: (_: any, record: any) => record.periodoLetivo?.dataInicio ? new Date(record.periodoLetivo.dataInicio).toLocaleDateString() : "-",
      sorter: (a: any, b: any) => new Date(a.periodoLetivo?.dataInicio ?? 0).getTime() - new Date(b.periodoLetivo?.dataInicio ?? 0).getTime(),
    },
    {
      title: "Data Fim",
      dataIndex: ["periodoLetivo", "dataFim"],
      key: "dataFim",
      render: (_: any, record: any) => record.periodoLetivo?.dataFim ? new Date(record.periodoLetivo.dataFim).toLocaleDateString() : "-",
      sorter: (a: any, b: any) => new Date(a.periodoLetivo?.dataFim ?? 0).getTime() - new Date(b.periodoLetivo?.dataFim ?? 0).getTime(),
    },
    {
      title: "Unidades",
      dataIndex: "unidades",
      key: "unidades",
      render: (_: any, record: any) => Array.isArray(record.unidades) && record.unidades.length > 0 ? record.unidades.map((u: any) => u.nome).join('; ') : "-",
      sorter: (a: any, b: any) => {
        const nomeA = Array.isArray(a.unidades) ? a.unidades.map((u: any) => u.nome).join('; ') : "";
        const nomeB = Array.isArray(b.unidades) ? b.unidades.map((u: any) => u.nome).join('; ') : "";
        return nomeA.localeCompare(nomeB);
      },
    },
    {
      title: "Status",
      dataIndex: "ativo",
      key: "ativo",
      render: (value: boolean, record: any) => (
        <Switch checked={!!value} onChange={() => toggleAtivo(record.id)} checkedChildren="Ativo" unCheckedChildren="Inativo" />
      ),
      sorter: (a: any, b: any) => Number(b.ativo) - Number(a.ativo),
    },
    {
      title: "Ação",
      key: "acao",
      render: (_: any, record: any) => (
        <Space size="middle">
          {canEdit(13) && (
            <AntdButton type="text" icon={<EditOutlined />} onClick={() => handleEdit(record.id)} />
          )}
        </Space>
      ),
    },
  ];

  return (
    <>
      <div className="mb-8">
        <span className="text-white text-2xl">Consulta de Turmas</span>
      </div>
      <ConfigProvider theme={{ algorithm: [theme.compactAlgorithm, theme.darkAlgorithm] }}>
        <div className="flex gap-8 items-end mb-8">
          <Input
            type="text"
            placeholder="Pesquisar por nome"
            className="w-80"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          {canInsert(13) && (
            <AntdButton className="flex gap-2" onClick={handleAdd}>
              Novo
              <PlusOutlined />
            </AntdButton>
          )}
        </div>
        <div className="grid">
          <div>
            <Table columns={columns} dataSource={filteredTurmas} rowKey="id" />
          </div>
        </div>
      </ConfigProvider>
    </>
  );
} 